import React from "react";
import { Tag } from "antd";

import {
  HOLD,
  ERROR,
  FAILED,
  PENDING,
  REJECTED,
  PROCESSED,
  CANCELLED,
} from "@api/services/transactions/shapes/TransactionStatusEnum";

export type StatusTransaction = {
  status: string;
  isApproved?: boolean;
  retryTransactionId?: string;
};

type Props = {
  transaction: StatusTransaction;
};

const RETRIED = "RETRIED";
const PENDING_APPROVAL = "PENDING_APPROVAL";

const COLORS_MAP: Record<string, string | null> = {
  [HOLD]: "orange",
  [ERROR]: "red",
  [FAILED]: "red",
  [RETRIED]: null,
  [PENDING]: "blue",
  [REJECTED]: "red",
  [CANCELLED]: null,
  [PROCESSED]: "green",
  [PENDING_APPROVAL]: "orange",
};

const LABELS_MAP: Record<string, string> = {
  [HOLD]: "Hold",
  [ERROR]: "Error",
  [FAILED]: "Failed",
  [RETRIED]: "Retried",
  [PENDING]: "Processing",
  [REJECTED]: "Rejected",
  [PROCESSED]: "Processed",
  [CANCELLED]: "Cancelled",
  [PENDING_APPROVAL]: "Pending Approval",
};

const getTransactionStatusLabel = (status: string): string => LABELS_MAP[status] || status;

const getTransactionStatusColor = (status: string): string | null | undefined => COLORS_MAP[status];

const computeStatus = (transaction: StatusTransaction): string => {
  const { status, isApproved, retryTransactionId } = transaction;

  const isHold = status === HOLD;
  const isPending = status === PENDING;
  const isPendingApproval = (isPending || isHold) && !isApproved;

  const isRetried = !!retryTransactionId;

  if (isPendingApproval) {
    return PENDING_APPROVAL;
  }

  if (isRetried) {
    return RETRIED;
  }

  return status;
};

const TransactionStatusTag = ({ transaction }: Props): JSX.Element => {
  const status = computeStatus(transaction);

  const label = getTransactionStatusLabel(status);
  const color = getTransactionStatusColor(status);

  return (
    <Tag
      color={color}
      bordered={false}
    >
      {label}
    </Tag>
  );
};

export default TransactionStatusTag;

export {
  computeStatus,
  getTransactionStatusLabel,
  getTransactionStatusColor,
};
